/**
 * ChatActionBar — 채팅 전용 액션 바
 *
 * 채팅 탭이 활성일 때 ChannelTabBar 아래에 표시.
 * 검색, 즐겨찾기, 공유, 필터, 넓게보기 버튼을 포함.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Search, Star, Share2, Languages } from 'lucide-react';
import FilterPopover from '../components/FilterPopover';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { cn } from '../../lib/utils';
import { useResponsive } from '../../hooks/useResponsive';
import { getAutoTranslate, setAutoTranslate, AutoTranslateEmitter } from '../../services/translation/autoTranslate';

// 라벨 포함 전체 버튼이 들어가지 못하는 패널 폭 기준 (최장인 ja 라벨 실측 + 여유). 미만이면 아이콘만 표시.
const COMPACT_THRESHOLD_PX = 720;
// 'min-width' 모드의 콘텐츠 상한(RoomContent 700px)이 실제로 걸리는 패널 폭 — 미만이면 넓게보기가 무의미해 숨김.
const WIDE_VIEW_MIN_PX = 740;

interface ChatActionBarProps {
  roomId: string;
  isFavoritesMode: boolean;
  contentWidth: 'min-width' | 'full';
  onSearchModeChange: (mode: boolean) => void;
  onFavoritesToggle: () => void;
  onSharesClick: () => void;
  onContentWidthChange: (mode: 'min-width' | 'full') => void;
  /** 분할 패널(채팅이 절반 폭)일 때 true — 넓게보기 토글을 폭과 무관하게 숨김 */
  isSplit?: boolean;
  /** 헤더 탭 자리에 inline 배치(개인 채널 전용) — border/배경 제거 */
  inHeader?: boolean;
}

const ChatActionBar: React.FC<ChatActionBarProps> = ({
  roomId,
  isFavoritesMode,
  contentWidth,
  onSearchModeChange,
  onFavoritesToggle,
  onSharesClick,
  onContentWidthChange,
  isSplit,
  inHeader,
}) => {
  const barRef = useRef<HTMLDivElement>(null);
  const { isMobileOrTablet } = useResponsive();
  // 채팅 패널 폭 측정 — compact(라벨 숨김) / canWiden(넓게보기 노출) 판정용
  const [barWidth, setBarWidth] = useState(0);
  const [autoOn, setAutoOn] = useState(() => getAutoTranslate(roomId));

  useEffect(() => {
    setAutoOn(getAutoTranslate(roomId));
    const h = (rid: string) => { if (rid === roomId) setAutoOn(getAutoTranslate(rid)); };
    AutoTranslateEmitter.on('change', h);
    return () => { AutoTranslateEmitter.removeListener('change', h); };
  }, [roomId]);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const measure = () => setBarWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 분할 모드 등으로 채팅 패널이 좁아지면 라벨을 숨기고 아이콘만 표시 (툴팁으로 대체)
  // 헤더 inline(개인 채널)은 컨테이너 폭이 콘텐츠만큼 좁게 측정돼 폭 기준이 무의미 →
  // viewport 기준으로 PC=라벨 표시, 모바일=아이콘만 (다른 채널 액션바와 통일).
  const compact = inHeader ? isMobileOrTablet : (barWidth > 0 && barWidth < COMPACT_THRESHOLD_PX);
  // 패널이 콘텐츠 상한보다 넓어 "더 넓게" 볼 수 있을 때만 넓게보기 토글 표시.
  // 단, 분할 패널(채팅이 절반 폭)에서는 폭과 무관하게 숨김 — 분할 중엔 넓게보기가 어색.
  // 헤더 inline(개인 채널)은 컨테이너 폭이 좁게 측정되므로 폭 기준 대신 viewport 기준 — PC 에서만 넓게보기 표시(모바일 제외).
  const canWiden = !isSplit && (inHeader ? !isMobileOrTablet : barWidth >= WIDE_VIEW_MIN_PX);

  return (
    // PC 전용 액션 바 — 모바일 ⋮ 팝오버처럼 아이콘 옆에 타이틀 표시 (filter-label = PC 라벨 버튼 표준)
    <div ref={barRef} className={cn('flex items-center gap-1.5 shrink-0', !inHeader && 'px-3 py-1.5 bg-muted/40 border-b border-border/50')}>
      <Button variant="filter-label" onClick={() => onSearchModeChange(true)} title={compact ? t`Search` : undefined}>
        <Search />
        {!compact && <Trans>Search</Trans>}
      </Button>

      <Button
        variant="filter-label"
        data-state={isFavoritesMode ? "on" : undefined}
        onClick={onFavoritesToggle}
        title={compact ? t`Favorites` : undefined}
      >
        <Star className={cn(isFavoritesMode && "fill-[rgb(var(--figma-gray-500))]")} />
        {!compact && <Trans>Favorites</Trans>}
      </Button>

      <Button variant="filter-label" onClick={onSharesClick} title={compact ? t`Shared Links` : undefined}>
        <Share2 />
        {!compact && <Trans>Shared Links</Trans>}
      </Button>

      {/* 메시지 타입 필터(사진/문서/음성) — 개인 채널에서도 유효 (내가 올린 사진/문서만 모아보기) */}
      <FilterPopover
        roomId={roomId}
        variant="filter-label"
        label={!compact && <Trans>Filter messages</Trans>}
      />

      <div className="flex-1" />

      {/* 자동 번역 토글 — 우측, 스위치로 ON/OFF 직관 표시 (채널 단위, 개인 설정) */}
      <div className="flex items-center gap-1.5 shrink-0">
        <Languages className="size-4 text-muted-foreground" />
        {!compact && <span className="text-label text-foreground whitespace-nowrap"><Trans>Auto-translate</Trans></span>}
        <Switch checked={autoOn} onCheckedChange={(v) => setAutoTranslate(roomId, v)} aria-label={t`Auto-translate`} />
      </div>

      {/* 넓게보기 토글 — 콘텐츠 상한(700px)이 실제로 걸릴 만큼 패널이 넓을 때만 */}
      {canWiden && (
        <Button
          variant="filter-label"
          onClick={() => onContentWidthChange(contentWidth === 'min-width' ? 'full' : 'min-width')}
          title={compact ? (contentWidth === 'full' ? t`Default View` : t`Wide View`) : undefined}
        >
          {contentWidth === 'full' ? (
            <img src="/assets/icon/wide-view.svg" className="size-4" alt="" />
          ) : (
            <img src="/assets/icon/compact-view.svg" className="size-4" alt="" />
          )}
          {!compact && (contentWidth === 'full' ? <Trans>Default View</Trans> : <Trans>Wide View</Trans>)}
        </Button>
      )}
    </div>
  );
};

export default ChatActionBar;
